import React, { useCallback, useMemo } from "react";
import { format } from "date-fns";
import { useRefreshRegister } from "../../components/RefreshProvider";
import useBreakpoints from "../../hooks/useBreakpoints";
import { IncreaseAwarenessPanelProps } from "./IncreaseAwarenessPanel.types";
import { usePostTrafficAwarenessGroupedQuery } from "../../app/useGeneratedApi";
import { OpenClickEventStatistics } from "../../app/GeneratedApi";
import { getLastPeriod } from "../../utils/dateUtils";
import { isToday } from "date-fns";

const formatMap: {
  [key in IncreaseAwarenessPanelProps["groupTimeUnit"]]: string;
} = {
  HOUR: "HH:mm",
  DAY: "d MMM",
  MONTH: "MMM yy",
  YEAR: "yyyy",
};

export function useIncreaseAwarenessPanel(props: IncreaseAwarenessPanelProps) {
  const { from, to, groupTimeUnit } = props;
  const { mdUp } = useBreakpoints();

  const {
    data: trafficAwarenessGroupedData,
    refetch,
    isLoading: isLoadingGroupedStatistics,
    error,
  } = usePostTrafficAwarenessGroupedQuery({
    from,
    to,
    groupTimeUnit,
    eventTypes: ["UNIQUE_OPEN"],
  });

  useRefreshRegister(async () => {
    await refetch();
  });

  const statistics = useMemo(() => {
    return (
      trafficAwarenessGroupedData?.uniqueOpenGroupedStatistics || {
        statistics: [],
        comparePercent: undefined,
        currentTotal: 0,
      }
    );
  }, [trafficAwarenessGroupedData]);

  const xValueAccessor = useCallback((item: OpenClickEventStatistics) => {
    return item.eventTime!;
  }, []);

  const yValueAccessor = useCallback((item: OpenClickEventStatistics) => {
    return item.currentCount!;
  }, []);

  const formatDate = useCallback(
    (date: string | Date) => {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return format(dateObj, formatMap[groupTimeUnit] || "MMM d");
    },
    [groupTimeUnit]
  );

  return {
    statistics,
    yValueAccessor,
    xValueAccessor,
    formatDate,
    mdUp,
    isLoadingGroupedStatistics,
    error,
    groupTimeUnit,
    lastPeriod: isToday(to || new Date())
      ? getLastPeriod(trafficAwarenessGroupedData?.compareDays || 0)
      : "period",
  };
}
